<template>
  <div data-view="users">
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>
    <layout-logged-frame
      :title="$t('internal_users')"
      :total-count="pagination.totalItems"
    >
      <template v-slot:header-actions>
        <Can action="store" subject="user">
          <v-btn
            color="primary"
            @click="$router.push({ name: 'internal-users-new', query: $route.query })"
          >
            <v-icon>add</v-icon>
            {{ $t('add_new_user') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:content>
        <UsersFilters v-if="hasFilters" @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :columns="columns"
          :items="users"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectUser"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'name'">
              {{ getUserName(scope.cell.item) }}
            </template>

            <template v-if="scope.cell.column.value === 'account_status.name'">
              <StatusPanel
                table-group="users"
                :status="scope.cell.item.account_status.name"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'role.name'">
              {{ displayRoleColumn(scope.cell.item) }}
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import UsersFilters from '../../components/forms/filters/InternalUsers';
  import urlPaginationRestore from '@/mixins/urlPaginationRestore';
  import StatusPanel from '../../components/panels/Status';
  import {extractNestedProp, isStr} from '../../utils';
  import columns from '../../config/users/columnsInternal';

  export default {
    mixins: [urlPaginationRestore],
    components: {
      UsersFilters,
      StatusPanel,
    },
    data() {
      return {
        columns,
        pagination: {
          page: 1,
          rowsPerPage: 15,
          sortBy: 'created_at',
          order: 'desc' ,
          totalItems: 0,
        },
        totalPages: 0,
        query: { per_page: 15, user_type: 1, pending: 0 },
        isLoading: false,
      };
    },
    computed: {
      users() {
        return this.$store.getters['users/internalItems'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      hasFilters() {
        return (
          this.$ability.can('see-user-status', 'filter') ||
          this.$ability.can('see-user-role', 'filter') ||
          this.$ability.can('see-user-search', 'filter') ||
          this.$ability.can('see-user-business', 'filter')
        );
      },
    },
    methods: {
      // Emitted by the dialog routes after a successful save. Keeps the
      // refetch in this view, with this view's own query, and out of the URL.
      onSaved() {
        this.getUsers(this.query);
      },
      getUserName(user) {
        const { firstname, lastname } = user;
        return `${firstname} ${lastname}`;
      },
      getUsers(query = {}) {
        this.isLoading = true;
        this.pagination.order = query.order;

        return this.$store
          .dispatch('users/getInternalItems', query)
          .then((res) => {
            const itemsMeta = this.$store.getters['users/internalItemsMeta'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      selectUser(user) {
        this.$store.dispatch('users/setActiveItem', user);
        this.$router.push({ name: 'internal-users-edit', params: { id: user.id }, query: this.$route.query });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;

        this.runInitialFetch(() => this.getUsers(nextQuery));
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;

        this.getUsers(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...filters,
          page: 1,
        };

        this.runInitialFetch(() => this.getUsers(this.query));
      },
      onClearFilters() {
        const { sortBy, order } = this.pagination;
        this.query = { sort: sortBy, order, per_page: 15, page: 1, user_type: 1, pending: 0 };

        this.getUsers(this.query);
      },
      displayRoleColumn(item) {
        return item.role.name;
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
  };
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

.v-datatable {
  min-width: $table-min-width;
}
</style>
